import { Suspense } from 'react'
import { Navigate } from 'react-router'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useUser } from 'hooks'

/**
 * Checks if every item in the allowedItems array is included in the userItems array.
 *
 * @param userItems - Items the current user has.
 * @param allowedItems - Items that must all be present.
 * @returns true if all allowedItems are in userItems.
 */
export const checkArrayEvery = (userItems: string[], allowedItems: string[]): boolean => {
  return allowedItems.every(item => userItems.includes(item))
}

/**
 * Checks if any of the allowedItems are present in the userItems array.
 *
 * @param userItems - Items the current user has.
 * @param allowedItems - Items where at least one must be present.
 * @returns true if any allowedItem is in userItems.
 */
export const checkArrayOneOf = (userItems: string[], allowedItems: string[]): boolean => {
  return allowedItems.some(item => userItems.includes(item))
}

/**
 * Route guard that checks the user's groups and features before rendering a protected page.
 * Falls back to a redirect when access is denied, or a loader while user data is loading.
 */
export const PrivateRoute = ({ children, path }: { children: React.ReactNode; path: string }) => {
  const { groups, networkAccess, features, user, ready } = useUser()

  if (ready && features.length) {
    if (groups && path.includes('/admin') && checkArrayOneOf(groups, ['admin', 'inpuDataManager'])) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/company-admin') &&
      groups &&
      features &&
      checkArrayEvery(features, ['companyAdmin']) &&
      checkArrayOneOf(groups, ['companyAdmin'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/employees') &&
      groups &&
      features &&
      checkArrayEvery(features, ['search']) &&
      checkArrayOneOf(groups, ['sales', 'admin'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/assignments') &&
      groups &&
      features &&
      checkArrayEvery(features, ['assignments']) &&
      checkArrayOneOf(groups, ['sales', 'freelancer'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/workorders') &&
      groups &&
      features &&
      checkArrayEvery(features, ['workOrders']) &&
      checkArrayOneOf(groups, ['sales', 'freelancer'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path === '/networks/add' &&
      networkAccess &&
      groups &&
      features &&
      checkArrayEvery(features, ['networks']) &&
      (checkArrayOneOf(groups, ['admin']) ||
        (user &&
          user.organizationId &&
          checkArrayOneOf(groups, ['companyAdmin']) &&
          user.Organization.allowCreateNetworks))
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      /\/networks\/\d*/.test(path) &&
      networkAccess &&
      groups &&
      features &&
      checkArrayEvery(features, ['networks']) &&
      (checkArrayOneOf(groups, ['admin']) ||
        (user &&
          user.organizationId &&
          checkArrayOneOf(groups, ['companyAdmin']) &&
          user.Organization.allowCreateNetworks))
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/networks/manage') && networkAccess && features && checkArrayEvery(features, ['networks'])) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path === '/networks/benefits') {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path === '/networks' &&
      !path.includes('/benefits') &&
      networkAccess &&
      features &&
      checkArrayEvery(features, ['networks'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/help')) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/statistics') &&
      groups &&
      features &&
      checkArrayEvery(features, ['reports']) &&
      checkArrayOneOf(groups, ['companyAdmin', 'admin', 'teamLeader'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/profile') &&
      features &&
      (checkArrayEvery(features, ['profile']) || checkArrayEvery(features, ['search']))
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/print') &&
      features &&
      (checkArrayEvery(features, ['profile']) || checkArrayEvery(features, ['search']))
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      (path.includes('/allocation/overview') || path.includes('/allocation/suppliers')) &&
      features &&
      checkArrayEvery(features, ['allocationOverview'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/allocation') &&
      features &&
      (checkArrayEvery(features, ['allocations']) || checkArrayEvery(features, ['search']))
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/teams') && features && checkArrayEvery(features, ['teams'])) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/scout') && features && checkArrayEvery(features, ['scout'])) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (
      path.includes('/salespool') &&
      groups &&
      features &&
      checkArrayEvery(features, ['salespool']) &&
      checkArrayOneOf(groups, ['sales'])
    ) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/unsubscribe')) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/notifications')) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/chat') && features && checkArrayEvery(features, ['chat'])) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    if (path.includes('/releases')) {
      return <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    }

    return <Navigate replace to="/" />
  } else {
    return <LoadingIndicator />
  }
}
